import { useState } from "react";
import { useNavigate } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { templatesApi, type EmailTemplate } from "@/api";
import { format } from "date-fns";
import { Plus, Save, Trash2, X, Code, Eye, Sparkles, Send } from "lucide-react";

export default function Templates() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<EmailTemplate | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [tab, setTab] = useState<"code" | "preview">("preview");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["templates"],
    queryFn: () => templatesApi.list(),
  });

  const saveMutation = useMutation({
    mutationFn: (tpl: {
      id?: string;
      name: string;
      subject: string;
      htmlBody: string;
      textBody?: string;
      kind?: "GENERIC" | "NEWSLETTER" | "FOLLOWUP";
      status?: "DRAFT" | "REVIEW" | "APPROVED" | "ARCHIVED";
      sourcePath?: string | null;
      topicKey?: string | null;
      notes?: string | null;
    }) => {
      if (tpl.id) return templatesApi.update(tpl.id, tpl);
      return templatesApi.create(tpl);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      setEditing(null);
      setIsNew(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => templatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      setEditing(null);
    },
  });

  const handleNew = () => {
    setEditing({
      id: "",
      name: "",
      subject: "",
      htmlBody: "",
      textBody: null,
      kind: "NEWSLETTER",
      status: "DRAFT",
      sourcePath: null,
      topicKey: null,
      notes: null,
      updatedAt: "",
    });
    setIsNew(true);
    setTab("code");
  };

  const handleEdit = async (id: string) => {
    const tpl = await templatesApi.get(id);
    setEditing(tpl);
    setIsNew(false);
    setTab("preview");
  };

  const generateWithAi = async () => {
    if (!aiPrompt.trim() || aiGenerating) return;
    setAiGenerating(true);
    setAiError("");
    try {
      const result = await templatesApi.generate(aiPrompt.trim());
      setEditing({
        id: "",
        name: result.name,
        subject: result.subject,
        htmlBody: result.htmlBody,
        textBody: result.textBody,
        kind: "NEWSLETTER",
        status: "DRAFT",
        sourcePath: null,
        topicKey: null,
        notes: null,
        updatedAt: "",
      });
      setIsNew(true);
      setTab("preview");
    } catch (err: any) {
      setAiError(err.message || "AI生成に失敗しました");
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSave = () => {
    if (!editing || !editing.name.trim() || !editing.subject.trim() || !editing.htmlBody.trim()) return;
    saveMutation.mutate({
      ...(isNew ? {} : { id: editing.id }),
      name: editing.name,
      subject: editing.subject,
      htmlBody: editing.htmlBody,
      textBody: editing.textBody || undefined,
      kind: editing.kind,
      status: editing.status,
      sourcePath: editing.sourcePath || null,
      topicKey: editing.topicKey || null,
      notes: editing.notes || null,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">メールテンプレート</h2>
        {!editing && (
          <button
            onClick={handleNew}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600"
          >
            <Plus size={16} />
            新規テンプレート
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-4">
          {/* AI generation */}
          <div className="bg-violet-50 rounded-lg border border-violet-200 p-4 space-y-3">
            <label className="flex items-center gap-1 font-bold text-sm text-violet-700">
              <Sparkles size={16} /> AIでテンプレートを生成
            </label>
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              rows={2}
              className="w-full border border-violet-200 rounded-md px-3 py-2 text-sm placeholder:text-violet-300"
              placeholder="例: RIZING LEDバルブの新商品案内メール。性能アップポイントと期間限定割引を訴求。CTAはカタログダウンロード。"
            />
            <div className="flex items-center gap-3">
              <button
                onClick={generateWithAi}
                disabled={!aiPrompt.trim() || aiGenerating}
                className="flex items-center gap-1 text-sm px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 disabled:opacity-50 transition-colors"
              >
                <Sparkles size={14} />
                {aiGenerating ? "生成中..." : "生成する"}
              </button>
              {aiError && <span className="text-sm text-red-600">{aiError}</span>}
            </div>
          </div>

          {/* Name + Subject */}
          <div className="bg-white p-4 rounded-lg shadow-sm border grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">テンプレート名</label>
              <input
                type="text"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="新商品案内テンプレート"
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">件名</label>
              <input
                type="text"
                value={editing.subject}
                onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                placeholder="件名を入力"
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">種別</label>
              <select
                value={editing.kind}
                onChange={(e) => setEditing({ ...editing, kind: e.target.value as EmailTemplate["kind"] })}
                className="w-full px-3 py-2 border rounded-md text-sm bg-white"
              >
                <option value="NEWSLETTER">メルマガ</option>
                <option value="FOLLOWUP">フォロー</option>
                <option value="GENERIC">汎用</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">状態</label>
              <select
                value={editing.status}
                onChange={(e) => setEditing({ ...editing, status: e.target.value as EmailTemplate["status"] })}
                className="w-full px-3 py-2 border rounded-md text-sm bg-white"
              >
                <option value="DRAFT">下書き</option>
                <option value="REVIEW">レビュー</option>
                <option value="APPROVED">承認済み</option>
                <option value="ARCHIVED">アーカイブ</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">トピック</label>
              <input
                type="text"
                value={editing.topicKey || ""}
                onChange={(e) => setEditing({ ...editing, topicKey: e.target.value || null })}
                placeholder="例: hid-led"
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">原稿ソース</label>
              <input
                type="text"
                value={editing.sourcePath || ""}
                onChange={(e) => setEditing({ ...editing, sourcePath: e.target.value || null })}
                placeholder="content/newsletter/..."
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>
          </div>

          {/* Editor + Preview side by side */}
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="flex items-center border-b bg-slate-50 px-4 py-2 gap-1">
              <button
                type="button"
                onClick={() => setTab("code")}
                className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  tab === "code" ? "bg-white shadow-sm text-slate-800" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Code size={12} /> HTML
              </button>
              <button
                type="button"
                onClick={() => setTab("preview")}
                className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  tab === "preview" ? "bg-white shadow-sm text-slate-800" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Eye size={12} /> プレビュー
              </button>
            </div>

            {tab === "code" ? (
              <textarea
                value={editing.htmlBody}
                onChange={(e) => setEditing({ ...editing, htmlBody: e.target.value })}
                rows={24}
                placeholder="<html><body>...</body></html>"
                className="w-full px-4 py-3 text-sm font-mono resize-y border-0 focus:outline-none"
              />
            ) : (
              <div className="bg-slate-100 p-4 flex justify-center" style={{ minHeight: 500 }}>
                <div className="bg-white shadow-lg rounded overflow-hidden" style={{ width: 620 }}>
                  {editing.htmlBody ? (
                    <iframe
                      srcDoc={editing.htmlBody}
                      sandbox=""
                      title="Template preview"
                      className="w-full border-0"
                      style={{ height: 700 }}
                    />
                  ) : (
                    <p className="text-slate-400 text-sm text-center py-20">HTMLを入力するとプレビューが表示されます</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Text version */}
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <label className="block text-xs font-medium text-slate-600 mb-1">テキスト版 (任意)</label>
            <textarea
              value={editing.textBody || ""}
              onChange={(e) => setEditing({ ...editing, textBody: e.target.value || null })}
              rows={4}
              placeholder="テキスト版の本文"
              className="w-full px-3 py-2 border rounded-md text-sm resize-y"
            />
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <label className="block text-xs font-medium text-slate-600 mb-1">運用メモ</label>
            <textarea
              value={editing.notes || ""}
              onChange={(e) => setEditing({ ...editing, notes: e.target.value || null })}
              rows={3}
              placeholder="訴求、承認メモ、次回改善案など"
              className="w-full px-3 py-2 border rounded-md text-sm resize-y"
            />
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleSave}
              disabled={saveMutation.isPending || !editing.name.trim() || !editing.subject.trim() || !editing.htmlBody.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-md text-sm hover:bg-slate-800 disabled:opacity-50"
            >
              <Save size={16} />
              {saveMutation.isPending ? "保存中..." : "保存"}
            </button>
            {!isNew && editing.id && (
              <button
                onClick={() => navigate(`/campaigns/new?templateId=${encodeURIComponent(editing.id)}`)}
                className="flex items-center gap-2 px-4 py-2 border rounded-md text-sm hover:bg-slate-50"
              >
                <Send size={16} />
                この原稿で配信を作成
              </button>
            )}
            {!isNew && (
              <button
                onClick={() => {
                  if (confirm("このテンプレートを削除しますか？")) deleteMutation.mutate(editing.id);
                }}
                disabled={deleteMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-md text-sm hover:bg-red-600 disabled:opacity-50"
              >
                <Trash2 size={16} />
                削除
              </button>
            )}
            <button
              onClick={() => { setEditing(null); setIsNew(false); }}
              className="flex items-center gap-2 px-4 py-2 border rounded-md text-sm hover:bg-slate-50"
            >
              <X size={16} />
              キャンセル
            </button>
          </div>

          {(saveMutation.isError || deleteMutation.isError) && (
            <p className="text-sm text-red-600">{(saveMutation.error || deleteMutation.error)?.message}</p>
          )}
        </div>
      ) : isLoading ? (
        <p className="text-slate-500">読み込み中...</p>
      ) : !data?.data.length ? (
        <div className="text-center py-12 text-slate-400">
          <p>テンプレートがありません</p>
          <p className="text-sm mt-1">「新規テンプレート」ボタンから作成してください</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.data.map((tpl) => (
            <div
              key={tpl.id}
              onClick={() => handleEdit(tpl.id)}
              className="bg-white rounded-lg shadow-sm border hover:border-blue-300 cursor-pointer transition-colors overflow-hidden"
            >
              {/* HTML Preview thumbnail */}
              <div className="relative bg-slate-50 border-b overflow-hidden" style={{ height: 180 }}>
                <iframe
                  srcDoc={tpl.htmlBody}
                  sandbox=""
                  title={tpl.name}
                  className="absolute top-0 left-0 border-0 pointer-events-none origin-top-left"
                  style={{ width: 600, height: 800, transform: "scale(0.42)" }}
                />
              </div>
              <div className="p-3">
                <h3 className="font-medium text-sm truncate">{tpl.name}</h3>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{tpl.subject}</p>
                <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                  <span>{tpl.kind === "NEWSLETTER" ? "メルマガ" : tpl.kind}</span>
                  <span>{tpl.status}</span>
                  {tpl.topicKey && <span>{tpl.topicKey}</span>}
                  {tpl._count && <span>{tpl._count.campaigns}配信</span>}
                </div>
                <p className="text-xs text-slate-400 mt-1">{format(new Date(tpl.updatedAt), "yyyy/MM/dd HH:mm")}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
